import { useState } from "react";
import { ArrowRight, FileText, Presentation, Package, Download, Sparkles, Check, ChevronDown, ChevronRight, Video, Mic, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface DeliveryOption {
  id: "script-first" | "slides-voiceover" | "course-in-a-box";
  title: string;
  subtitle: string;
  icon: typeof FileText;
  description: string;
  features: string[];
  steps: string[];
  reassurance: string[];
  downloadLabel: string;
}

const deliveryOptions: DeliveryOption[] = [
  {
    id: "script-first",
    title: "Script-First Video Course",
    subtitle: "Faceless & Flexible",
    icon: Video,
    description: "Your scripts are word-for-word ready for faceless videos. No camera needed!",
    features: [
      "All modules and lessons complete",
      "Full video scripts for every lesson",
      "Ready for AI or recorded voiceover",
    ],
    steps: [
      "Use scripts as voiceovers (AI voice or your own voice)",
      "Add visuals: stock footage, B-roll, or text on screen",
      "Create videos in any editor (CapCut, Canva, Premiere Pro)",
      "Export and upload to your course platform",
    ],
    reassurance: [
      "No face required",
      "No advanced editing skills needed",
      "Use any tools you already know",
    ],
    downloadLabel: "Download Scripts + Visual Notes",
  },
  {
    id: "slides-voiceover",
    title: "Slides + Voiceover Course",
    subtitle: "Beginner-Friendly",
    icon: Presentation,
    description: "The easiest option! Get structured slides with matching voiceover scripts.",
    features: [
      "Slide titles for each lesson",
      "On-screen bullet points",
      "Matching voiceover scripts",
    ],
    steps: [
      "Upload slides into Canva, PowerPoint, or Google Slides",
      "Add the provided voiceover script (record or use AI)",
      "Export each lesson as a video",
      "Upload to your course platform",
    ],
    reassurance: [
      "Perfect for complete beginners",
      "No video editing experience needed",
      "Works with free tools",
    ],
    downloadLabel: "Download Slides + Voiceover Scripts",
  },
  {
    id: "course-in-a-box",
    title: "Course-in-a-Box",
    subtitle: "Complete Product Package",
    icon: Package,
    description: "Everything packaged and ready to sell—even without videos!",
    features: [
      "Video scripts included",
      "Slide outlines ready",
      "Workbooks & checklists",
      "Lesson summaries",
      "Sales page copy",
    ],
    steps: [
      "Download your complete course package",
      "Upload content to any platform",
      "Start selling immediately (videos optional)",
      "Hire an editor later if you want professional videos",
    ],
    reassurance: [
      "Product is already monetizable",
      "Videos can be added later",
      "Everything you need to launch",
    ],
    downloadLabel: "Download Complete Course Package",
  },
];

interface CourseDeliveryOptionsProps {
  content: any;
  lessonScripts: Record<string, string>;
  product: any;
  project: any;
  onExport: (type: "script-first" | "slides-voiceover" | "course-in-a-box") => void;
  onContinue: () => void;
}

export function CourseDeliveryOptions({
  content,
  lessonScripts,
  product,
  project,
  onExport,
  onContinue,
}: CourseDeliveryOptionsProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [expandedOption, setExpandedOption] = useState<string | null>(null);

  const allScriptsGenerated = content?.modules?.every((module: any, i: number) =>
    module.lessons?.every((_: any, j: number) => lessonScripts[`${i}-${j}`])
  );

  if (!allScriptsGenerated) {
    return null;
  }

  return (
    <div className="glass-card p-6 border-green-500/30 mb-6 bg-gradient-to-br from-green-500/5 to-transparent">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 text-sm font-medium mb-3">
          <Sparkles className="w-4 h-4" />
          Your Course is Complete!
        </div>
        <h2 className="font-display text-2xl font-bold mb-2">Choose Your Delivery Method</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Your full course is ready with all modules, lessons, and scripts. Now choose how you want to turn it into something you can sell.
          <span className="block mt-2 text-sm text-green-400">No experience required—pick what feels easiest for you!</span>
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {deliveryOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedOption === option.id;
          const isExpanded = expandedOption === option.id;

          return (
            <Card
              key={option.id}
              className={cn(
                "cursor-pointer transition-all duration-300 border-2 hover:shadow-lg",
                isSelected
                  ? "border-green-500 bg-green-500/10 shadow-green-500/20 shadow-lg"
                  : "border-border/50 hover:border-green-500/50"
              )}
              onClick={() => {
                setSelectedOption(option.id);
                setExpandedOption(option.id);
              }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                    isSelected ? "bg-green-500 text-white" : "bg-muted"
                  )}>
                    <Icon className="w-6 h-6" />
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <CardTitle className="text-lg mt-3">
                  {option.title}
                  <span className="block text-sm font-normal text-green-400 mt-0.5">
                    {option.subtitle}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{option.description}</p>

                <div className="space-y-2 mb-4">
                  {option.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedOption(isExpanded ? null : option.id);
                  }}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {isExpanded ? "Hide details" : "See step-by-step guide"}
                </button>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div>
                      <p className="text-xs font-medium text-green-400 mb-2">Step-by-step:</p>
                      <ol className="space-y-1.5">
                        {option.steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center shrink-0 text-[10px] font-medium">
                              {i + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <p className="text-xs font-medium text-green-400 mb-1.5">Don't worry:</p>
                      <ul className="space-y-1">
                        {option.reassurance.map((item, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-green-500" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedOption && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4 border-t border-border/50 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Button
            variant="outline"
            size="lg"
            onClick={() => onExport(selectedOption as any)}
            className="w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            {deliveryOptions.find(o => o.id === selectedOption)?.downloadLabel}
          </Button>
          <Button
            variant="hero"
            size="lg"
            onClick={onContinue}
            className="w-full sm:w-auto"
          >
            Continue to Pricing
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground mt-4">
        Which option would you like to use? Select one above to get started!
      </p>
    </div>
  );
}
